/* eslint-disable react/require-default-props */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from 'store';
import { AiManagementCard, AiManagementState } from 'store/modules/ai_management_store/types';
import { getTeam, upsertCard } from 'store/modules/ai_management_store/actions';
import { listUpdates, markUpdatesRead } from 'services/actions/ai_management_updates';
import { BidderFileGeneratedHandler, uploadBidderFile } from 'services/actions/ai_management_files';
import { buildBiddingUpdates } from 'services/ai_management_bidding_updates';
import AiManagementMembersModal from 'components/molecules/ai_management_members_modal';
import AiManagementCardModalTopbar from 'components/molecules/ai_management_card_modal_topbar';
import AiManagementCardModalSidebar from 'components/molecules/ai_management_card_modal_sidebar';
import AiManagementPanel from 'components/molecules/ai_management_panel';
import AiManagementBiddingInfo from 'components/organisms/ai_management_bidding_info';
import AiManagementBiddingNotes from 'components/molecules/ai_management_bidding_notes';
import AiManagementBiddingFiles from 'components/molecules/ai_management_bidding_files';
import AiManagementBidderFiles from 'components/molecules/ai_management_bidder_files';
import AiManagementBiddingTasks from 'components/molecules/ai_management_bidding_tasks';
import AiManagementBiddingUpdates from 'components/molecules/ai_management_bidding_updates';
import AiManagementBiddingChat from 'components/molecules/ai_management_bidding_chat';
import AiManagementBiddingRobot from 'components/molecules/ai_management_bidding_robot';
import AiManagementBiddingAnalysis from 'components/organisms/ai_management_bidding_analysis';
import AiManagementBiddingDocuments from 'components/organisms/ai_management_bidding_documents';
import AiManagementBiddingProposal from 'components/organisms/ai_management_bidding_proposal';
import AiManagementHiringHistory from 'components/molecules/ai_management_hiring_history';
import AiManagementPossibleCompetitors from 'components/molecules/ai_management_possible_competitors';
import AiManagementContractAnalysis from 'components/organisms/ai_management_contract_analysis';
import { useBiddingDetail } from 'hooks/useBiddingDetail';
import { useBidderFiles } from 'hooks/useBidderFiles';
import { useEditalFiles } from 'hooks/useEditalFiles';
import { useManualCardUpdates } from 'hooks/useManualCardUpdates';
import { SIDEBAR_GROUPS } from 'services/ai_management_card_modal';
import { Backdrop, Panel } from './styles';

const FULL_AREA_SECTIONS = [
  'anotacoes',
  'tarefas',
  'atualizacoes',
  'mensagens_chat',
  'analise_edital',
  'historico_contratacao',
  'possiveis_concorrentes',
  'preparar_documentacao',
  'elaborar_proposta',
];

interface AiManagementCardModalProps {
  card: AiManagementCard;
  initialAction?: 'new_task' | 'new_update' | 'updates';
  onClose: () => void;
}

const SECTION_BY_ACTION: Record<string, string> = {
  new_task: 'tarefas',
  new_update: 'atualizacoes',
  updates: 'atualizacoes',
};

const AiManagementCardModal: React.FC<AiManagementCardModalProps> = ({
  card,
  initialAction,
  onClose,
}) => {
  const newTask = initialAction === 'new_task';
  const [activeSection, setActiveSection] = useState(
    (initialAction && SECTION_BY_ACTION[initialAction]) || 'painel',
  );
  const [pendingNewTask, setPendingNewTask] = useState(newTask);
  const [pendingNewUpdate, setPendingNewUpdate] = useState(initialAction === 'new_update');
  const [pendingItem, setPendingItem] = useState<{ section: string; id: number | string } | null>(
    null,
  );
  const [closing, setClosing] = useState(false);
  const [membersModalOpen, setMembersModalOpen] = useState(false);
  const dispatch = useDispatch();

  const selectSection = (section: string, itemId?: number | string) => {
    setPendingNewTask(false);
    setPendingNewUpdate(false);
    setPendingItem(itemId === undefined ? null : { section, id: itemId });
    setActiveSection(section);
  };

  const columns = useSelector<ApplicationState, AiManagementState['columns']>(
    (state) => state.session.ai_management.columns,
  );
  const teamRequest = useSelector<ApplicationState, AiManagementState['request']['team']>(
    (state) => state.session.ai_management.request.team,
  );

  useEffect(() => {
    if (!teamRequest.loaded && !teamRequest.loading) dispatch(getTeam());
  }, []);
  const column = columns.find((item) => item.id === card.column_id);

  const biddingDetail = useBiddingDetail(card.bidding_id);
  const editalFiles = useEditalFiles(card.bidding_id);
  const bidderFiles = useBidderFiles(card.bidding_id);
  const manualUpdates = useManualCardUpdates(card.id);
  const { reload: reloadBidderFiles } = bidderFiles;

  const handleBidderFileGenerated = useCallback<BidderFileGeneratedHandler>(
    (source, file, filename) => {
      uploadBidderFile(card.bidding_id, source, file, filename)
        .then(reloadBidderFiles)
        .catch((err) => {
          console.error(
            `Erro ao salvar o arquivo do licitante da licitacao ${card.bidding_id}:`,
            err,
          );
          window.alert(
            'O arquivo foi baixado, mas não foi salvo em "Arquivos gerados pelo licitante".',
          );
        });
    },
    [card.bidding_id, reloadBidderFiles],
  );

  const updatesCount = useMemo(
    () => buildBiddingUpdates(biddingDetail.bidding, manualUpdates.updates).length,
    [biddingDetail.bidding, manualUpdates.updates],
  );

  const sidebarCounts = {
    anotacoes: biddingDetail.bidding?.client_comments?.length ?? card.counts.comments,
    arquivos_edital:
      editalFiles.loading || editalFiles.error ? undefined : editalFiles.files.length,
    tarefas: biddingDetail.bidding?.tasks?.length ?? card.counts.tasks,
    atualizacoes: biddingDetail.bidding ? updatesCount : card.counts.updates,
    arquivos_licitante:
      bidderFiles.loading || bidderFiles.error ? undefined : bidderFiles.files.length,
  };

  const handleClose = () => setClosing(true);
  const handleAnimationEnd = () => {
    if (closing) onClose();
  };

  useEffect(() => {
    if (activeSection !== 'atualizacoes' || !card.counts.updates) return;

    listUpdates({ card_id: card.id, read: false, per_page: 100 })
      .then((response) => {
        const ids: number[] = (response?.data?.updates ?? []).map(
          (update: { id: number }) => update.id,
        );
        if (ids.length === 0) return undefined;
        return markUpdatesRead(ids);
      })
      .then(() => {
        dispatch(upsertCard({ ...card, counts: { ...card.counts, updates: 0 } }));
      })
      .catch(() => undefined);
  }, [activeSection, card, dispatch]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setClosing(true);
    };
    document.addEventListener('keydown', handleKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  const activeLabel =
    SIDEBAR_GROUPS.flatMap((group) => group.items).find((item) => item.key === activeSection)
      ?.label ?? 'Painel';

  const renderSection = () => {
    switch (activeSection) {
      case 'anotacoes':
        return (
          <AiManagementBiddingNotes
            bidding={biddingDetail.bidding}
            loading={biddingDetail.loading}
            error={biddingDetail.error}
            patch={biddingDetail.patch}
          />
        );
      case 'painel':
        return (
          <AiManagementPanel
            bidding={biddingDetail.bidding}
            comments={biddingDetail.bidding?.client_comments ?? []}
            manualUpdates={manualUpdates.updates}
            counts={card.counts}
            loading={biddingDetail.loading}
            error={biddingDetail.error}
            onNavigate={(target, itemId) => target && selectSection(target, itemId)}
          />
        );
      case 'informacoes':
        return (
          <AiManagementBiddingInfo
            bidding={biddingDetail.bidding}
            loading={biddingDetail.loading}
            error={biddingDetail.error}
          />
        );
      case 'arquivos_edital':
        return (
          <AiManagementBiddingFiles
            files={editalFiles.files}
            loading={editalFiles.loading}
            error={editalFiles.error}
          />
        );
      case 'arquivos_licitante':
        return (
          <AiManagementBidderFiles
            files={bidderFiles.files}
            loading={bidderFiles.loading}
            error={bidderFiles.error}
          />
        );
      case 'tarefas':
        return (
          <AiManagementBiddingTasks
            bidding={biddingDetail.bidding}
            loading={biddingDetail.loading}
            error={biddingDetail.error}
            patch={biddingDetail.patch}
            openNewTask={pendingNewTask}
            initialTaskId={pendingItem?.section === 'tarefas' ? Number(pendingItem.id) : undefined}
          />
        );
      case 'atualizacoes':
        return (
          <AiManagementBiddingUpdates
            bidding={biddingDetail.bidding}
            loading={biddingDetail.loading || manualUpdates.loading}
            error={biddingDetail.error}
            manualUpdates={manualUpdates.updates}
            onCreateUpdate={manualUpdates.create}
            onAddNote={manualUpdates.addNote}
            openNewUpdate={pendingNewUpdate}
            initialUpdateId={
              pendingItem?.section === 'atualizacoes' ? String(pendingItem.id) : undefined
            }
          />
        );
      case 'analise_edital':
        return (
          <AiManagementBiddingAnalysis
            bidding={biddingDetail.bidding}
            loading={biddingDetail.loading}
            error={biddingDetail.error}
            files={editalFiles.files}
            filesLoading={editalFiles.loading}
            filesError={editalFiles.error}
            onFileGenerated={handleBidderFileGenerated}
          />
        );
      case 'historico_contratacao':
        return (
          <AiManagementHiringHistory
            biddingId={card.bidding_id}
            publicBodyName={card.bidding?.public_body ?? null}
            noticeNumber={card.bidding?.notice_number ?? null}
            onFileGenerated={handleBidderFileGenerated}
            onOpenBidderFiles={() => selectSection('arquivos_licitante')}
          />
        );
      case 'possiveis_concorrentes':
        return (
          <AiManagementPossibleCompetitors
            biddingId={card.bidding_id}
            publicBodyName={card.bidding?.public_body ?? null}
            noticeNumber={card.bidding?.notice_number ?? null}
            onFileGenerated={handleBidderFileGenerated}
            onOpenBidderFiles={() => selectSection('arquivos_licitante')}
          />
        );
      case 'preparar_documentacao':
        return (
          <AiManagementBiddingDocuments
            bidding={biddingDetail.bidding}
            loading={biddingDetail.loading}
            error={biddingDetail.error}
          />
        );
      case 'elaborar_proposta':
        return (
          <AiManagementBiddingProposal
            bidding={biddingDetail.bidding}
            loading={biddingDetail.loading}
            error={biddingDetail.error}
            onBidderFilesChanged={reloadBidderFiles}
          />
        );
      case 'mensagens_chat':
        return (
          <AiManagementBiddingChat
            bidding={biddingDetail.bidding}
            loading={biddingDetail.loading}
            error={biddingDetail.error}
          />
        );
      case 'analise_clausulas':
        return (
          <AiManagementContractAnalysis
            bidding={biddingDetail.bidding}
            loading={biddingDetail.loading}
            error={biddingDetail.error}
            onFileGenerated={handleBidderFileGenerated}
          />
        );
      case 'robo_lances':
        return (
          <AiManagementBiddingRobot
            bidding={biddingDetail.bidding}
            loading={biddingDetail.loading}
            error={biddingDetail.error}
          />
        );
      default:
        return (
          <div className="text-muted">
            Conteúdo em construção — os dados entram na próxima etapa.
          </div>
        );
    }
  };

  return createPortal(
    <>
      <Backdrop closing={closing} onClick={handleClose} />
      <Panel closing={closing} onAnimationEnd={handleAnimationEnd}>
        <AiManagementCardModalTopbar
          card={card}
          column={column}
          onOpenMembers={() => setMembersModalOpen(true)}
          onClose={handleClose}
        />

        <div className="card-modal-body d-flex">
          <AiManagementCardModalSidebar
            activeSection={activeSection}
            counts={sidebarCounts}
            onSelect={selectSection}
          />
          <main className="card-modal-main d-flex flex-column bg-light">
            {FULL_AREA_SECTIONS.includes(activeSection) ? (
              renderSection()
            ) : (
              <div className="card-modal-section overflow-auto p-4">
                <h4 className="mb-4">{activeLabel}</h4>
                {renderSection()}
              </div>
            )}
          </main>
        </div>
      </Panel>

      <AiManagementMembersModal
        card={card}
        show={membersModalOpen}
        onClose={() => setMembersModalOpen(false)}
      />
    </>,
    document.body,
  );
};

export default AiManagementCardModal;
